import React, { useEffect, useRef, useState } from "react";
import { Button } from "newui/components/Button";
import { InputWithLabel } from "newui/components/ui/InputWithLabel";

export interface TotpStartStepProps {
	error?: string;
	isSubmitting: boolean;
	onSubmit: (password: string) => void;
}

export const TotpStartStep = ({ error, isSubmitting, onSubmit }: TotpStartStepProps) => {
	const [password, setPassword] = useState("");
	const passwordRef = useRef<HTMLInputElement>(null);

	// PRO-731 follow-up — focus the password box as soon as this step is shown.
	//
	// Deliberately NOT the host modal's `initialFocusRef`: this step is nested
	// inside TotpEnrolmentWizard, which is itself nested inside three different
	// modals (TwoFactorAuthModal > MfaSettingsSection, RecoveryReenrolModal,
	// Enforced2faSetupModal). In two of those the wizard isn't even mounted when
	// its modal opens — MfaSettingsSection decides, and RecoveryReenrolModal only
	// mounts it once the user clicks "Set up new authenticator" — so
	// `initialFocusRef`, which fires at modal open, would never reach this field.
	//
	// Deferring to the next frame (rather than a synchronous focus at mount, or
	// the native `autoFocus` attribute) is the same fix OtpInput uses for the
	// step that follows this one: focus doesn't stick while the browser is still
	// settling layout for a freshly-swapped-in subtree, and it also lands after
	// Chakra's focus lock has done its own initial focus, so the lock doesn't
	// steal it straight back.
	useEffect(() => {
		const raf = requestAnimationFrame(() => passwordRef.current?.focus());
		return () => cancelAnimationFrame(raf);
	}, []);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		onSubmit(password);
	};

	return (
		<form onSubmit={handleSubmit} className="flex flex-col gap-4 !p-0">
			<p className="font-roboto text-sm text-gray-600">
				Confirm your password to generate a two-factor authentication QR code.
			</p>
			<InputWithLabel
				label="Password"
				type="password"
				icon
				inputRef={passwordRef}
				value={password}
				onChange={(e) => setPassword(e.target.value)}
				errorText={error}
				testId="totp-setup-password-input"
				required
			/>
			<Button
				type="submit"
				btntype="primary"
				disabled={!password || isSubmitting}
				isLoading={isSubmitting}
				data-testid="enable-2fa-continue-btn"
			>
				Continue
			</Button>
		</form>
	);
};
